<template>
  <div class="global-search-container">
    <zm-button
      v-if="isMobileViewport"
      :icon="Magnifier"
      type="secondary"
      :aria-label="$t('aria.search')"
      @click="showSearchPopover" />

    <zm-button
      v-else
      class="global-search-header-button"
      :aria-expanded="dialogVisible"
      aria-haspopup="dialog"
      aria-controls="global-search-dialog"
      aria-keyshortcuts="Meta+K Control+K"
      :icon="Magnifier"
      type="secondary"
      neutral
      :aria-label="$t('aria.search')"
      @click="showSearchPopover">
      {{ $t('search') }}
      <span aria-hidden="true">({{ shortcutHint }})</span>
    </zm-button>

    <zm-dialog
      id="global-search-dialog"
      v-model="dialogVisible"
      :aria-label="$t('aria.search')"
      :teleported="false"
      size=""
      close-on-click-modal
      class="global-search-dialog"
      @close="handleClearQuery">
      <template #header>
        <form class="global-search-form" role="search" @submit.prevent>
          <zm-input
            ref="searchInputRef"
            v-model="query"
            class="global-search-input"
            size="lg"
            :placeholder="placeholder"
            :aria-label="placeholderAria"
            :lead-icon="Magnifier"
            bare
            @change="clearKeyboardEventState">
            <template #append-select>
              <zm-button
                v-show="query.length"
                type="tertiary"
                class="global-search-clear-btn"
                @click="query = ''">
                {{ $t('clear') }}
              </zm-button>

              <zm-button
                :icon="Close"
                type="tertiary"
                class="global-search-close-btn"
                :aria-label="$t('aria.close')"
                @click="closeSearchPopover" />
            </template>
          </zm-input>
        </form>
      </template>

      <zm-tabs v-if="showUserTab || showSettingsTab" v-model="activeName" class="mgt-md">
        <zm-tab-pane v-if="showUserTab" name="users">
          <template #label>
            <span>{{ $t('users') }}</span>
            <zm-badge
              v-if="hasSearched"
              type="counter"
              :count="counters.users"
              size="lg"
              :is-blue="activeName === 'users'"
              :is-grey="activeName !== 'users'" />
          </template>
        </zm-tab-pane>

        <zm-tab-pane v-if="showSettingsTab" name="settings">
          <template #label>
            <span>{{ $t('settings') }}</span>
            <zm-badge
              v-if="hasSearched"
              type="counter"
              :count="counters.settings"
              size="lg"
              :is-blue="activeName === 'settings'"
              :is-grey="activeName !== 'settings'" />
          </template>
        </zm-tab-pane>

        <zm-tab-pane name="navigation">
          <template #label>
            <span>{{ $t('navigation') }}</span>
            <zm-badge
              v-if="hasSearched"
              type="counter"
              :count="counters.navigation"
              size="lg"
              :is-blue="activeName === 'navigation'"
              :is-grey="activeName !== 'navigation'" />
          </template>
        </zm-tab-pane>
      </zm-tabs>

      <div v-show="hasSearched" class="gs-result-container">
        <users-pane v-if="showUserTab" ref="usersPaneRef" v-show="activeName === 'users'" />

        <settings-pane
          v-if="showSettingsTab"
          ref="settingsPaneRef"
          v-show="activeName === 'settings'" />

        <navigation-pane
          ref="navigationPaneRef"
          v-show="(!showUserTab && !showSettingsTab) || activeName === 'navigation'" />
      </div>

      <div v-show="!hasSearched" class="gs-guide">
        <img src="/assets/magnifying-glass.png" width="120" height="120" alt="" />
        <p class="gs-guide-tips">{{ $t('searchGuide') }}</p>
      </div>
    </zm-dialog>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { Magnifier, Close } from '@zoom/icons-vue'
import {
  ZmInput,
  ZmDialog,
  ZmButton,
  ZmTabs,
  ZmTabPane,
  ZmBadge,
  hasClass,
  removeClass
} from '@zoom/zoom-ui-vue3'
import { trackEvent } from '@/shared/event-tracker'
import usersPane from './users.vue'
import settingsPane from './settings.vue'
import navigationPane from './navigation.vue'

const { t } = useI18n()

const props = defineProps({
  isMobileViewport: Boolean
})

const SEARCH_DEBOUNCE_MS = 300
const AUTO_SWITCH_DELAY_MS = 100
const ADMIN_MENU_LOADED_EVENT = 'admin-side-menu-loaded'

const isSearching = ref(false)

const dialogVisible = ref(false)
const query = ref('')
const hasSearched = ref(false)
const tabOverride = ref('')
const menusData = ref([])

const usersPaneRef = ref(null)
const settingsPaneRef = ref(null)
const navigationPaneRef = ref(null)
const searchInputRef = ref(null)

const counters = reactive({
  users: 0,
  settings: 0,
  navigation: 0
})

const searchedTabs = reactive(new Set())

let debounceTimer = 0
let autoSwitchTimer = 0
let searchToken = 0
const idleCancels = new Set()

const syncMenus = () => {
  const menus = window.sideMenu?.menusData
  menusData.value = Array.isArray(menus) ? menus : []
}

const showUserTab = computed(() => {
  return menusData.value.some(
    (menu) =>
      menu.id === 'sidemenu-user-management' &&
      menu.items?.some((item) => item.id === 'user-menu-item-users')
  )
})

const showSettingsTab = computed(() => {
  return menusData.value.some((menu) => menu.id === 'settings-policies')
})

const visibleTabs = computed(() => [
  ...(showUserTab.value ? ['users'] : []),
  ...(showSettingsTab.value ? ['settings'] : []),
  'navigation'
])

const activeName = computed({
  get() {
    const tabs = visibleTabs.value

    if (tabOverride.value && tabs.includes(tabOverride.value)) {
      return tabOverride.value
    }

    return tabs[0] || 'navigation'
  },

  set(name) {
    tabOverride.value = name
  }
})

const placeholderScope = computed(() => {
  if (showUserTab.value && showSettingsTab.value) return 'All'
  if (showUserTab.value) return 'Users'
  if (showSettingsTab.value) return 'Settings'
  return 'Nav'
})

const placeholder = computed(() => t(`searchPlaceholder${placeholderScope.value}`))

const placeholderAria = computed(() => t(`aria.searchPlaceholder${placeholderScope.value}`))

const shortcutHint =
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || '') ||
  (navigator.userAgent || '').includes('Mac')
    ? '⌘K'
    : 'Ctrl+K'

const resetCounters = () => {
  counters.users = 0
  counters.settings = 0
  counters.navigation = 0
}

const resetSearchResults = () => {
  hasSearched.value = false
  searchedTabs.clear()
  resetCounters()
}

const resetSearchUi = () => {
  tabOverride.value = ''
  resetSearchResults()
}

const showSearchPopover = () => {
  syncMenus()
  dialogVisible.value = true
  trackEvent({
    linkText: 'start search',
    linkUrl: 'undefined',
    uiLocation: 'web portal global search'
  })
}

const closeSearchPopover = () => {
  dialogVisible.value = false
}

const handleClearQuery = () => {
  query.value = ''
  cancelSearch()
  resetSearchUi()
}

const isSearchHotkey = (event) =>
  !event.repeat &&
  !event.altKey &&
  !event.shiftKey &&
  (event.metaKey || event.ctrlKey) &&
  event.code === 'KeyK'

const onGlobalKeydown = (event) => {
  if (!isSearchHotkey(event)) return

  event.preventDefault()
  showSearchPopover()
}

const clearKeyboardEventState = () => {
  if (hasClass(document.body, 'is-vue3-keyboard-event')) {
    removeClass(document.body, 'is-vue3-keyboard-event')
  }
}

const scheduleIdle = (callback) => {
  let cancelled = false
  let id

  const run = (...args) => {
    if (cancelled) return

    idleCancels.delete(cancel)
    callback(...args)
  }

  const cancel = () => {
    cancelled = true
    idleCancels.delete(cancel)

    if (typeof window.cancelIdleCallback === 'function' && typeof id === 'number') {
      window.cancelIdleCallback(id)
    } else {
      window.clearTimeout(id)
    }
  }

  if (typeof window.requestIdleCallback === 'function') {
    id = window.requestIdleCallback(run)
  } else {
    id = window.setTimeout(run, 1)
  }

  idleCancels.add(cancel)
}

const cancelIdleWork = () => {
  idleCancels.forEach((cancel) => cancel())
  idleCancels.clear()
}

const cancelDebounce = () => {
  if (!debounceTimer) return

  window.clearTimeout(debounceTimer)
  debounceTimer = 0
}

const cancelAutoSwitch = () => {
  if (!autoSwitchTimer) return

  window.clearTimeout(autoSwitchTimer)
  autoSwitchTimer = 0
}

const invalidateSearch = () => {
  searchToken += 1
  cancelIdleWork()

  Object.values(tabRefs).forEach((paneRef) => {
    paneRef.value?.cancel?.()
  })

  return searchToken
}

const cancelSearch = () => {
  cancelDebounce()
  cancelAutoSwitch()
  invalidateSearch()
}

const tabRefs = {
  users: usersPaneRef,
  settings: settingsPaneRef,
  navigation: navigationPaneRef
}

const searchTab = async (tabName, token) => {
  const pane = tabRefs[tabName]?.value

  if (!pane || token !== searchToken) return

  try {
    counters[tabName] = (await pane.search(query.value)) || 0
  } catch (error) {
    if (token !== searchToken) return

    console.error(`Search ${tabName} failed:`, error)
    counters[tabName] = 0
  } finally {
    if (token === searchToken) {
      searchedTabs.add(tabName)
    }
  }
}

const focusSearchInput = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      searchInputRef.value?.focus?.()
    })
  })
}

const switchToFirstResultTab = () => {
  if (counters[activeName.value] > 0) return

  const targetTab = visibleTabs.value.find((tab) => counters[tab] > 0)

  if (!targetTab) return

  tabOverride.value = targetTab

  focusSearchInput()
}

const scheduleAutoSwitch = () => {
  cancelAutoSwitch()

  autoSwitchTimer = window.setTimeout(() => {
    autoSwitchTimer = 0

    if (searchToken) {
      switchToFirstResultTab()
    }
  }, AUTO_SWITCH_DELAY_MS)
}

const runSearch = async () => {
  const token = invalidateSearch()
  const tabs = visibleTabs.value

  searchedTabs.clear()
  resetCounters()
  hasSearched.value = true
  isSearching.value = true

  if (!tabOverride.value) {
    tabOverride.value = tabs[0] || 'navigation'
  }

  const firstTab = tabs[0]

  if (firstTab) {
    await searchTab(firstTab, token)
  }

  if (token !== searchToken) return

  await runIdleSearches(tabs.slice(1), token)

  if (token === searchToken) {
    isSearching.value = false
  }
}
const runIdleSearches = async (tabs, token) => {
  for (const tabName of tabs) {
    if (token !== searchToken) return

    await new Promise((resolve) => {
      scheduleIdle(() => {
        searchTab(tabName, token).finally(resolve)
      })
    })
  }

  if (token === searchToken) {
    scheduleAutoSwitch()
  }
}

watch(query, (value) => {
  const keyword = value?.trim() ?? ''

  cancelDebounce()
  cancelAutoSwitch()

  if (!keyword) {
    cancelSearch()
    resetSearchResults()
    focusSearchInput()
    return
  }

  debounceTimer = window.setTimeout(() => {
    debounceTimer = 0
    runSearch()
  }, SEARCH_DEBOUNCE_MS)
})

watch(activeName, (tabName) => {
  if (isSearching.value || debounceTimer || !query.value.trim() || searchedTabs.has(tabName)) {
    return
  }

  searchTab(tabName, searchToken)
})

watch(dialogVisible, (visible) => {
  if (!visible) return

  syncMenus()

  focusSearchInput()
})

onMounted(() => {
  syncMenus()

  window.addEventListener('keydown', onGlobalKeydown, true)
  window.addEventListener(ADMIN_MENU_LOADED_EVENT, syncMenus)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown, true)
  window.removeEventListener(ADMIN_MENU_LOADED_EVENT, syncMenus)

  cancelSearch()
})
</script>

<style lang="scss">
.global-search-container {
  flex: 1;
  text-align: center;
  flex-grow: 100;
}
.global-search-header-button {
  width: 320px;
  color: var(--zoom-color-text-strong-neutral, #555b62);
}
.is-mobile-viewport {
  .global-search-dialog {
    width: 100%;
    margin-left: 0;
    left: 0;
    top: 0;
    border-radius: 0;
    bottom: 0;
  }
}
.global-search-dialog {
  position: absolute;
  left: 50%;
  width: 900px;
  margin-left: -450px;
  top: 16px;
  border-radius: 12px;
  border: 1px solid var(--zoom-color-border-subtle-neutral, #dfe3e8);
  .fe-popups-dialog__header {
    padding: 6px 16px;
    line-height: 32px;
    border-bottom: 1px solid var(--zoom-color-border-subtle-neutral, #dfe3e8);
    .fe-popups-input__inner.is-leaded {
      padding-left: 22px;
    }
  }
  .gs-guide {
    padding-top: 80px;
    padding-bottom: 80px;
    text-align: center;
    img {
      width: 120px;
      height: 120px;
    }
    .gs-guide-tips {
      margin-top: 8px;
      color: var(--zoom-color-text-text-strong-neutral, #555b62);
    }
  }
  .fe-popups-dialog__body {
    padding: 0;
    &.is-lost-footer {
      margin-bottom: 0;
    }
  }
  .fe-popups-tabs__nav-wrap {
    margin-left: 24px;
    margin-right: 24px;
  }
  .gs-result-container {
    padding: 12px 24px;
    max-height: 480px;
    overflow: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--zoom-color-fill-contrary-transparent) transparent;
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--zoom-color-fill-contrary-transparent);
      border-radius: 999px;
    }
    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }

  .gs-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    flex-direction: column;
    .empty-sub {
      color: var(--zoom-color-text-strong-neutral, #555b62);
      font-size: 14px;
      line-height: 18px;
      margin-top: 16px;
    }
    .empty-main {
      color: var(--zoom-color-text-stronger-neutral, #222325);
      font-size: 16px;
      line-height: 20px;
    }
  }

  .gs-results-list {
    list-style: none;
    padding: 0;
    margin: 0 -8px;

    .gs-result-item {
      border-radius: 8px;
      padding: 8px 12px;
      cursor: pointer;
      transition: background-color 0.2s;
      &:hover,
      &:focus-visible {
        text-decoration: none;
        background-color: var(--zoom-color-state-subtle-neutral-hover, rgba(110, 118, 128, 0.12));
      }
      &:focus-visible {
        outline: 2px solid var(--zoom-color-border-primary);
        outline-offset: 2px;
      }
    }
  }

  .gs-setting-section,
  .gs-navigation-section {
    text-align: left;

    .gs-setting-name,
    .gs-navigation-name {
      margin: 0 0 4px;
      line-height: 20px;
      font-weight: 600;
      color: var(--zoom-color-text-stronger-neutral, #323539);
    }

    .gs-setting-description,
    .gs-navigation-path {
      margin: 0;
      line-height: 16px;
      font-size: 12px;
      color: var(--zoom-color-text-strong-neutral, #555b62);
    }
  }
  .fe-popups-highlight-text {
    mark {
      padding: 0;
    }
  }
}
.fe-popups-overlay:has(.global-search-dialog) {
  background-color: rgba(255, 255, 255, 0);
}
.global-search-form {
  width: 100%;
  min-width: 0;
}
.global-search-input {
  .fe-popups-input__leading {
    left: 0;
  }
  .fe-popups-input__append-select {
    position: relative;
  }
  .global-search-clear-btn {
    color: var(--zoom-color-text-neutral, #6e7680);
    margin-right: 8px;
  }
  .global-search-close-btn {
    box-sizing: content-box;
    margin-left: 8px;
    &:before {
      position: absolute;
      left: -8px;
      top: 0;
      bottom: 0;
      content: '';
      border-left: 1px solid var(--zoom-color-border-subtle-neutral, #dfe3e8);
    }
  }
}
</style>
